import { isEmptySlateValue, validateEachLanguage } from "@dgs/core";
import { FormikErrors, useFormikContext } from "formik";
import { TFunction } from "i18next";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { EventBaseDataFormValues, EventFormTranslationValues, EventFormValues } from "../../types/event";
import { EventBaseDataStepFormFields } from "./EventBaseDataStepFormFields";
import { EventFormCard } from "./EventFormCard";
import { useSubmitEventStep } from "./eventStepperUtils";

export function getEventBaseDataStepValidator(t: TFunction) {
	return (values: EventBaseDataFormValues): FormikErrors<EventBaseDataFormValues> => {
		const errors: FormikErrors<EventFormValues> = {};

		if (!values.eventType) {
			errors.eventType = t("Field is required");
		}
		if (!values.start) {
			errors.start = t("Field is required");
		}
		if (!values.end) {
			errors.end = t("Field is required");
		}
		if (values.end && values.start && values.start >= values.end) {
			errors.end = t("End has to be after start");
		}
		if (!values.primaryLanguage) {
			errors.primaryLanguage = t("Field is required");
		}
		if (values.eventType?.eventLanguageRequirement === "required" && values.languages.length === 0) {
			errors.languages = t("Field is required");
		}
		if (values.eventType?.organizerRequirement === "required" && !values.organizer) {
			errors.organizer = t("Field is required");
		}
		if (values.eventType?.locationRequirement === "required" && !values.location) {
			errors.location = t("Field is required");
		}
		if (!values.visibility) {
			errors.visibility = t("Field is required");
		}

		return {
			...errors,
			...validateEachLanguage(values.translations, (_, translations) => {
				const errors: { [K in keyof EventFormTranslationValues]?: any } = {};
				if (!translations.name) {
					errors.name = t("Field is required");
				}

				if (
					values.eventType?.additionalInfoRequirement === "required" &&
					(!translations.additionalInfo || isEmptySlateValue(translations.additionalInfo))
				) {
					errors.additionalInfo = t("Field is required");
				}

				return errors;
			}),
		};
	};
}

export const isEventBaseDataStepValid = (errors: FormikErrors<EventBaseDataFormValues>) => {
	return (
		!errors.translations &&
		!errors.eventType &&
		!errors.start &&
		!errors.end &&
		!errors.primaryLanguage &&
		!errors.organizer &&
		!errors.location &&
		!errors.languages &&
		!errors.visibility
	);
};

interface Props {
	variant?: "elevated" | "outlined";
}

export const EventBaseDataStep: FC<Props> = ({ variant }) => {
	const { t } = useTranslation();
	const { values, setTouched, touched } = useFormikContext<EventBaseDataFormValues>();
	const submitEventStep = useSubmitEventStep();

	const handleNextClick = useCallback(async () => {
		const errors = await setTouched({
			...touched,
			eventType: true,
			start: true,
			end: true,
			timezone: true,
			primaryLanguage: true,
			languages: true as any,
			organizer: true,
			visibility: true,
			image: true,
			location: true,
			translations: values.translations.map(() => ({
				additionalInfo: true,
				name: true,
			})),
		});

		if (errors && isEventBaseDataStepValid(errors)) {
			await submitEventStep(values.eventType, "base-data");
		}
	}, [setTouched, submitEventStep, touched, values.eventType, values.translations]);

	return (
		<EventFormCard variant={variant} title={t("Base data")} onNextClick={handleNextClick} showLanguageSwitcher={true}>
			<EventBaseDataStepFormFields />
		</EventFormCard>
	);
};
